import { FC } from 'react';
import type { UserCourse } from '@teachur/http-client';

interface StudentCourseStatsProps {
  userCourse: UserCourse;
  currentStep: string;
}

type UserCourseWithProgressMeta = UserCourse & {
  startedAt?: string | null;
  lastActivityAt?: string | null;
};

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
};

const formatDate = (date?: string | null) => {
  if (!date) return '—';

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return '—';

  return parsedDate.toLocaleDateString('en-US', DATE_FORMAT_OPTIONS);
};

export const StudentCourseStats: FC<StudentCourseStatsProps> = ({ userCourse, currentStep }) => {
  const courseWithProgress = userCourse as UserCourseWithProgressMeta;

  const hasStartedCourse = userCourse.started || userCourse.passed;

  const startedCourseDate = hasStartedCourse
    ? (courseWithProgress.startedAt ?? courseWithProgress.createdAt)
    : null;

  const lastActivityDate = hasStartedCourse
    ? (courseWithProgress.lastActivityAt ?? courseWithProgress.updatedAt)
    : null;

  const stats = [
    {
      label: 'Current step',
      value: currentStep,
    },
    {
      label: 'Started course',
      value: formatDate(startedCourseDate),
    },
    {
      label: 'Last activity',
      value: formatDate(lastActivityDate),
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 rounded-xl bg-(--neutral-white-NW1000) sm:grid-cols-2 xl:grid-cols-3">
      {stats.map((stat) => (
        <div
          key={stat.label}
          className="flex flex-col gap-3 rounded-2xl border border-(--neutral-white-NW800) bg-(--neutral-white-NW1000) p-4 shadow-sm"
        >
          <span className="[font-size:var(--fs-l5)] font-semibold uppercase text-(--neutral-black-NB600)">
            {stat.label}
          </span>

          <p className="[font-size:var(--fs-p2)] m-0! font-semibold text-(--neutral-black-NB1000)">
            {stat.value}
          </p>
        </div>
      ))}
    </div>
  );
};
